import React, { useEffect, useRef, useState } from 'react'
import pdfjsLib from '../utils/pdfjsWrapper.js'
import { API_BASE_URL, resolveFileUrl } from '../config/api'
// El worker ya está configurado en main.jsx y en el wrapper

const PDFViewer = ({ file, currentPage, onPageChange, totalPages, signatures, onRemoveSignature, onUpdateSignature, onScaleChange, campos = [], onUpdateCampo, onRemoveCampo, onDropFirmante, vistaFirma = null, notas = [], onUpdateNota, onRemoveNota, onDropNota }) => {
  const canvasRef = useRef(null)
  const scrollContainerRef = useRef(null)
  /** Ratio del centro del viewport (solo tras +/- zoom); se aplica tras render del canvas */
  const preserveViewportCenterRatioRef = useRef(null)
  const [pdfDoc, setPdfDoc] = useState(null)
  // Referencia UI del 100%: mismo tamaño PDF.js que antes con base 1.5 al mostrar ~150% (1.5×1.5)
  const REAL_SCALE = 2.25
  const [scale, setScale] = useState(REAL_SCALE)
  const [isLoading, setIsLoading] = useState(true)
  const [draggedSignature, setDraggedSignature] = useState(null)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [resizingSignature, setResizingSignature] = useState(null)
  const [resizeHandle, setResizeHandle] = useState(null) // 'nw', 'ne', 'sw', 'se'
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 })
  const [draggedCampo, setDraggedCampo] = useState(null)
  const [campoDragOffset, setCampoDragOffset] = useState({ x: 0, y: 0 })
  const [resizingCampo, setResizingCampo] = useState(null)
  const [campoResizeHandle, setCampoResizeHandle] = useState(null)
  const [campoResizeStart, setCampoResizeStart] = useState({ x: 0, y: 0, boxX: 0, boxY: 0, width: 0, height: 0 })
  const [firmanteSobrePdf, setFirmanteSobrePdf] = useState(false)
  const [notaSobrePdf, setNotaSobrePdf] = useState(false)
  const [draggedNota, setDraggedNota] = useState(null)
  const [notaDragOffset, setNotaDragOffset] = useState({ x: 0, y: 0 })
  const [resizingNota, setResizingNota] = useState(null)
  const [notaResizeHandle, setNotaResizeHandle] = useState(null)
  const [notaResizeStart, setNotaResizeStart] = useState({ x: 0, y: 0, boxX: 0, boxY: 0, width: 0, height: 0 })
  const [canvasDimensions, setCanvasDimensions] = useState({
    width: 0,
    height: 0,
    scale: REAL_SCALE,
    displayWidth: 0,
    displayHeight: 0
  })
  const [systemConfig, setSystemConfig] = useState({
    show_signature_area_border: true,
    show_pdf_info_label: true,
    show_calibration_grid: false,
    show_calibration_markers: false
  })

  useEffect(() => {
    loadSystemConfig()
    loadPDF()
  }, [file])

  const loadSystemConfig = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/config/system`)
      if (response.ok) {
        const config = await response.json()
        setSystemConfig(config)
      }
    } catch (error) {
      console.error('Error al cargar configuración del sistema:', error)
      // Usar valores por defecto si hay error
    }
  }

  useEffect(() => {
    if (pdfDoc) {
      renderPage()
    }
  }, [pdfDoc, currentPage, scale])

  // Notificar cambios de escala y dimensiones mostradas al componente padre
  useEffect(() => {
    if (onScaleChange) {
      // Limitar scale a máximo ~250% en UI (REAL_SCALE * 2.5)
      const MAX_SCALE = REAL_SCALE * 2.5
      const limitedScale = Math.min(scale, MAX_SCALE)
      onScaleChange({
        scale: limitedScale,
        contentWidth: canvasDimensions.width || null,
        contentHeight: canvasDimensions.height || null
      })
    }
  }, [scale, canvasDimensions.width, canvasDimensions.height, onScaleChange])

  const loadPDF = async () => {
    try {
      setIsLoading(true)
      let pdf
      
      if (typeof file === 'string') {
        // Si es una URL, reemplazar URL de producción con localhost en desarrollo
        const pdfUrl = resolveFileUrl(file)
        
        // Si es una URL, cargar directamente
        try {
          pdf = await pdfjsLib.getDocument(pdfUrl).promise
        } catch (urlError) {
          // Si es un error 404, intentar verificar si la URL está mal formada
          if (urlError.name === 'MissingPDFException' || urlError.message?.includes('404')) {
            console.error('PDF no encontrado en:', file)
            // El error se propaga para que el componente padre lo maneje
            throw new Error(`El archivo PDF no se encuentra en el servidor: ${file}`)
          }
          throw urlError
        }
      } else {
        // Si es un File object, convertir a arrayBuffer
        const arrayBuffer = await file.arrayBuffer()
        pdf = await pdfjsLib.getDocument(arrayBuffer).promise
      }
      
      setPdfDoc(pdf)
    } catch (error) {
      console.error('Error al cargar PDF:', error)
      // No establecer pdfDoc si hay error, para que el componente padre pueda manejar el error
      setPdfDoc(null)
    } finally {
      setIsLoading(false)
    }
  }

  const renderPage = async () => {
    if (!pdfDoc) return

    try {
      const page = await pdfDoc.getPage(currentPage)
      const canvas = canvasRef.current
      
      if (!canvas) return

      const context = canvas.getContext('2d')
      // Limitar scale a máximo ~250% en UI (REAL_SCALE * 2.5)
      const MAX_SCALE = REAL_SCALE * 2.5
      const limitedScale = Math.min(scale, MAX_SCALE)
      const viewport = page.getViewport({ scale: limitedScale })
      
      // Establecer dimensiones del canvas (bitmap = layout CSS 1:1; evita estirado por contenedor)
      canvas.width = viewport.width
      canvas.height = viewport.height
      canvas.style.width = `${viewport.width}px`
      canvas.style.height = `${viewport.height}px`
      setCanvasDimensions({ width: viewport.width, height: viewport.height, scale: limitedScale })

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      }

      // Cancelar cualquier renderizado anterior
      if (canvas._currentRenderTask) {
        canvas._currentRenderTask.cancel()
      }

      const renderTask = page.render(renderContext)
      canvas._currentRenderTask = renderTask

      await renderTask.promise
      canvas._currentRenderTask = null
      const rect = canvas.getBoundingClientRect()
      setCanvasDimensions({
        width: viewport.width,
        height: viewport.height,
        scale: limitedScale,
        displayWidth: rect.width,
        displayHeight: rect.height
      })
      setIsLoading(false)

      const container = scrollContainerRef.current
      if (container) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const sw = container.scrollWidth
            const cw = container.clientWidth
            if (sw <= cw) {
              container.scrollLeft = 0
              return
            }
            const centerRatio = preserveViewportCenterRatioRef.current
            if (centerRatio != null) {
              preserveViewportCenterRatioRef.current = null
              const target = centerRatio * sw - cw / 2
              const maxScroll = sw - cw
              container.scrollLeft = Math.max(0, Math.min(target, maxScroll))
            } else {
              // Primera carga o cambio de página: centrar si hay overflow horizontal
              container.scrollLeft = Math.max(0, (sw - cw) / 2)
            }
          })
        })
      }
    } catch (error) {
      // Ignorar errores de cancelación de renderizado (son normales al cambiar de página)
      if (error.name === 'RenderingCancelledException') {
        preserveViewportCenterRatioRef.current = null
      } else {
        console.error('Error al renderizar página:', error)
      }
      setIsLoading(false)
    }
  }

  const captureViewportCenterRatioBeforeZoom = () => {
    const container = scrollContainerRef.current
    if (!container || container.scrollWidth <= 0) {
      preserveViewportCenterRatioRef.current = null
      return
    }
    const prevScrollLeft = container.scrollLeft
    const prevScrollWidth = container.scrollWidth
    const prevClientWidth = container.clientWidth
    preserveViewportCenterRatioRef.current =
      (prevScrollLeft + prevClientWidth / 2) / prevScrollWidth
  }

  const handleZoomIn = () => {
    captureViewportCenterRatioBeforeZoom()
    setScale(prev => {
      // Incremento del 10% relativo a REAL_SCALE
      const increment = REAL_SCALE * 0.1
      const MAX_SCALE = REAL_SCALE * 2.5 // ~250% máximo en UI
      const newScale = Math.min(prev + increment, MAX_SCALE)
      if (onScaleChange) onScaleChange(newScale)
      return newScale
    })
  }

  const handleZoomOut = () => {
    captureViewportCenterRatioBeforeZoom()
    setScale(prev => {
      // Decremento del 10% relativo a REAL_SCALE
      const decrement = REAL_SCALE * 0.1
      const newScale = Math.max(prev - decrement, REAL_SCALE * 0.3)
      if (onScaleChange) onScaleChange(newScale)
      return newScale
    })
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1)
    }
  }

  const getClientCoordinates = (e) => {
    // Para eventos táctiles, usar el primer touch
    if (e.touches && e.touches.length > 0) {
      return {
        clientX: e.touches[0].clientX,
        clientY: e.touches[0].clientY
      }
    }
    // Para eventos de mouse
    return {
      clientX: e.clientX,
      clientY: e.clientY
    }
  }

  const handleMouseDown = (e, signature) => {
    // No iniciar arrastre si se hace clic en el botón de eliminar
    // Si se hace clic en el handle de redimensionamiento, iniciar resize
    if (e.target.classList.contains('resize-handle')) {
      e.preventDefault()
      e.stopPropagation()
      const handle = e.target.dataset.handle
      setResizingSignature(signature)
      setResizeHandle(handle)
      const rect = e.currentTarget.getBoundingClientRect()
      const coords = getClientCoordinates(e)
      setResizeStart({
        x: coords.clientX,
        y: coords.clientY,
        width: signature.width || (signature.type === 'imagen' ? 200 : 200),
        height: signature.height || (signature.type === 'imagen' ? 67 : 34)
      })
      return
    }
    e.preventDefault()
    e.stopPropagation()
    setDraggedSignature(signature)
    const rect = e.currentTarget.getBoundingClientRect()
    const coords = getClientCoordinates(e)
    setDragOffset({
      x: coords.clientX - rect.left,
      y: coords.clientY - rect.top
    })
  }

  const handleTouchStart = (e, signature) => {
    e.preventDefault()
    e.stopPropagation()
    setDraggedSignature(signature)
    const rect = e.currentTarget.getBoundingClientRect()
    const coords = getClientCoordinates(e)
    setDragOffset({
      x: coords.clientX - rect.left,
      y: coords.clientY - rect.top
    })
  }

  const handleMouseMove = (e) => {
    // Manejar redimensionamiento
    if (resizingSignature && resizeHandle) {
      e.preventDefault()
      const coords = getClientCoordinates(e)
      const deltaX = coords.clientX - resizeStart.x
      const deltaY = coords.clientY - resizeStart.y
      
      let newWidth = resizeStart.width
      let newHeight = resizeStart.height
      
      // Calcular nuevo tamaño según el handle arrastrado
      // CRÍTICO: Mantener X,Y fijos - solo cambiar width/height
      switch (resizeHandle) {
        case 'se': // Esquina inferior derecha
          newWidth = Math.max(20, resizeStart.width + deltaX)
          newHeight = Math.max(20, resizeStart.height + deltaY)
          break
        case 'sw': // Esquina inferior izquierda
          newWidth = Math.max(20, resizeStart.width - deltaX)
          newHeight = Math.max(20, resizeStart.height + deltaY)
          break
        case 'ne': // Esquina superior derecha
          newWidth = Math.max(20, resizeStart.width + deltaX)
          newHeight = Math.max(20, resizeStart.height - deltaY)
          break
        case 'nw': // Esquina superior izquierda
          newWidth = Math.max(20, resizeStart.width - deltaX)
          newHeight = Math.max(20, resizeStart.height - deltaY)
          break
      }
      
      // Para imágenes, mantener aspect ratio si es necesario
      if (resizingSignature.type === 'imagen' && resizeStart.width > 0 && resizeStart.height > 0) {
        const aspectRatio = resizeStart.width / resizeStart.height
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          // Cambio principalmente horizontal
          newHeight = newWidth / aspectRatio
        } else {
          // Cambio principalmente vertical
          newWidth = newHeight * aspectRatio
        }
      }
      
      // Actualizar firma con nuevo tamaño (X,Y permanecen iguales)
      if (onUpdateSignature) {
        onUpdateSignature(resizingSignature.id, {
          ...resizingSignature,
          width: Math.round(newWidth),
          height: Math.round(newHeight)
        })
      }
      return
    }
    
    // Manejar arrastre
    if (!draggedSignature) return
    
    e.preventDefault()
    const rect = canvasRef.current.getBoundingClientRect()
    const coords = getClientCoordinates(e)
    const newX = coords.clientX - rect.left - dragOffset.x
    const newY = coords.clientY - rect.top - dragOffset.y
    
    // Actualizar posición de la firma
    if (onUpdateSignature) {
      onUpdateSignature(draggedSignature.id, {
        ...draggedSignature,
        x: Math.max(0, Math.min(newX, rect.width - (draggedSignature.width || 200))),
        y: Math.max(0, Math.min(newY, rect.height - (draggedSignature.height || 34)))  // 34px para acomodar 26px (1.6rem) con padding
      })
    }
  }

  const handleTouchMove = (e) => {
    if (!draggedSignature) return
    
    e.preventDefault()
    e.stopPropagation()
    handleMouseMove(e)
  }

  const handleMouseUp = () => {
    setDraggedSignature(null)
    setDragOffset({ x: 0, y: 0 })
    setResizingSignature(null)
    setResizeHandle(null)
    setResizeStart({ x: 0, y: 0, width: 0, height: 0 })
  }

  const handleTouchEnd = (e) => {
    e.preventDefault()
    e.stopPropagation()
    handleMouseUp()
  }

  useEffect(() => {
    if (draggedSignature || resizingSignature) {
      document.addEventListener('mousemove', handleMouseMove, { passive: false })
      document.addEventListener('mouseup', handleMouseUp)
      document.addEventListener('touchmove', handleTouchMove, { passive: false })
      document.addEventListener('touchend', handleTouchEnd, { passive: false })
      document.addEventListener('touchcancel', handleTouchEnd, { passive: false })
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.removeEventListener('touchmove', handleTouchMove)
        document.removeEventListener('touchend', handleTouchEnd)
        document.removeEventListener('touchcancel', handleTouchEnd)
      }
    }
  }, [draggedSignature, dragOffset, resizingSignature, resizeHandle, resizeStart])

  const campoPixels = (campo) => {
    const rect = canvasRef.current ? canvasRef.current.getBoundingClientRect() : null
    const width = rect && rect.width ? rect.width : canvasDimensions.width
    const height = rect && rect.height ? rect.height : canvasDimensions.height
    return {
      width,
      height,
      x: campo.xRatio * width,
      y: campo.yRatio * height,
      w: campo.wRatio * width,
      h: campo.hRatio * height
    }
  }

  const commitCampoBox = (campo, box) => {
    if (!onUpdateCampo || !box.width || !box.height) return
    const maxX = Math.max(0, box.width - box.w)
    const maxY = Math.max(0, box.height - box.h)
    const x = Math.max(0, Math.min(box.x, maxX))
    const y = Math.max(0, Math.min(box.y, maxY))
    const w = Math.max(24, Math.min(box.w, box.width - x))
    const h = Math.max(24, Math.min(box.h, box.height - y))
    onUpdateCampo(campo.id, {
      ...campo,
      xRatio: x / box.width,
      yRatio: y / box.height,
      wRatio: w / box.width,
      hRatio: h / box.height
    })
  }

  const handleCampoMouseDown = (e, campo) => {
    if (campo.bloqueado) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    if (e.target.classList.contains('campo-resize-handle')) {
      e.preventDefault()
      e.stopPropagation()
      const box = campoPixels(campo)
      const coords = getClientCoordinates(e)
      setResizingCampo(campo)
      setCampoResizeHandle(e.target.dataset.handle)
      setCampoResizeStart({
        x: coords.clientX,
        y: coords.clientY,
        boxX: box.x,
        boxY: box.y,
        width: box.w,
        height: box.h
      })
      return
    }
    e.preventDefault()
    e.stopPropagation()
    const box = campoPixels(campo)
    const coords = getClientCoordinates(e)
    const canvasRect = canvasRef.current.getBoundingClientRect()
    setDraggedCampo(campo)
    setCampoDragOffset({
      x: coords.clientX - canvasRect.left - box.x,
      y: coords.clientY - canvasRect.top - box.y
    })
  }

  const handleCampoMouseMove = (e) => {
    if (!canvasRef.current) return
    const canvasRect = canvasRef.current.getBoundingClientRect()
    const coords = getClientCoordinates(e)
    if (resizingCampo && campoResizeHandle) {
      e.preventDefault()
      const deltaX = coords.clientX - campoResizeStart.x
      const deltaY = coords.clientY - campoResizeStart.y
      let x = campoResizeStart.boxX
      let y = campoResizeStart.boxY
      let w = campoResizeStart.width
      let h = campoResizeStart.height
      if (campoResizeHandle === 'se') {
        w = campoResizeStart.width + deltaX
        h = campoResizeStart.height + deltaY
      } else if (campoResizeHandle === 'sw') {
        w = campoResizeStart.width - deltaX
        h = campoResizeStart.height + deltaY
        x = campoResizeStart.boxX + (campoResizeStart.width - Math.max(24, w))
      } else if (campoResizeHandle === 'ne') {
        w = campoResizeStart.width + deltaX
        h = campoResizeStart.height - deltaY
        y = campoResizeStart.boxY + (campoResizeStart.height - Math.max(24, h))
      } else if (campoResizeHandle === 'nw') {
        w = campoResizeStart.width - deltaX
        h = campoResizeStart.height - deltaY
        x = campoResizeStart.boxX + (campoResizeStart.width - Math.max(24, w))
        y = campoResizeStart.boxY + (campoResizeStart.height - Math.max(24, h))
      }
      commitCampoBox(resizingCampo, { x, y, w, h, width: canvasRect.width, height: canvasRect.height })
      return
    }
    if (!draggedCampo) return
    e.preventDefault()
    commitCampoBox(draggedCampo, {
      x: coords.clientX - canvasRect.left - campoDragOffset.x,
      y: coords.clientY - canvasRect.top - campoDragOffset.y,
      w: draggedCampo.wRatio * canvasRect.width,
      h: draggedCampo.hRatio * canvasRect.height,
      width: canvasRect.width,
      height: canvasRect.height
    })
  }

  const handleCampoMouseUp = () => {
    setDraggedCampo(null)
    setCampoDragOffset({ x: 0, y: 0 })
    setResizingCampo(null)
    setCampoResizeHandle(null)
  }

  useEffect(() => {
    if (!draggedCampo && !resizingCampo) return undefined
    document.addEventListener('mousemove', handleCampoMouseMove)
    document.addEventListener('mouseup', handleCampoMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleCampoMouseMove)
      document.removeEventListener('mouseup', handleCampoMouseUp)
    }
  }, [draggedCampo, campoDragOffset, resizingCampo, campoResizeHandle, campoResizeStart])

  const notaPixels = (nota) => {
    const width = canvasRef.current ? canvasRef.current.getBoundingClientRect().width : 0
    const height = canvasRef.current ? canvasRef.current.getBoundingClientRect().height : 0
    return {
      x: nota.xRatio * width,
      y: nota.yRatio * height,
      w: nota.wRatio * width,
      h: nota.hRatio * height,
      width,
      height
    }
  }

  const commitNotaBox = (nota, box) => {
    if (!onUpdateNota || nota.guardada || !box.width || !box.height) return
    const maxX = Math.max(0, box.width - box.w)
    const maxY = Math.max(0, box.height - box.h)
    const x = Math.max(0, Math.min(box.x, maxX))
    const y = Math.max(0, Math.min(box.y, maxY))
    const w = Math.max(80, Math.min(box.w, box.width - x))
    const h = Math.max(64, Math.min(box.h, box.height - y))
    onUpdateNota(nota.id, {
      ...nota,
      xRatio: x / box.width,
      yRatio: y / box.height,
      wRatio: w / box.width,
      hRatio: h / box.height
    })
  }

  const handleNotaMouseDown = (e, nota) => {
    if (nota.guardada) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    if (e.target.classList.contains('nota-resize-handle')) {
      e.preventDefault()
      e.stopPropagation()
      const box = notaPixels(nota)
      const coords = getClientCoordinates(e)
      setResizingNota(nota)
      setNotaResizeHandle(e.target.dataset.handle)
      setNotaResizeStart({
        x: coords.clientX,
        y: coords.clientY,
        boxX: box.x,
        boxY: box.y,
        width: box.w,
        height: box.h
      })
      return
    }
    e.preventDefault()
    e.stopPropagation()
    const box = notaPixels(nota)
    const coords = getClientCoordinates(e)
    const canvasRect = canvasRef.current.getBoundingClientRect()
    setDraggedNota(nota)
    setNotaDragOffset({
      x: coords.clientX - canvasRect.left - box.x,
      y: coords.clientY - canvasRect.top - box.y
    })
  }

  const handleNotaMouseMove = (e) => {
    if (!canvasRef.current) return
    const canvasRect = canvasRef.current.getBoundingClientRect()
    const coords = getClientCoordinates(e)
    if (resizingNota && notaResizeHandle) {
      e.preventDefault()
      const deltaX = coords.clientX - notaResizeStart.x
      const deltaY = coords.clientY - notaResizeStart.y
      let x = notaResizeStart.boxX
      let y = notaResizeStart.boxY
      let w = notaResizeStart.width
      let h = notaResizeStart.height
      if (notaResizeHandle === 'se') {
        w = notaResizeStart.width + deltaX
        h = notaResizeStart.height + deltaY
      } else if (notaResizeHandle === 'sw') {
        w = notaResizeStart.width - deltaX
        h = notaResizeStart.height + deltaY
        x = notaResizeStart.boxX + (notaResizeStart.width - Math.max(80, w))
      } else if (notaResizeHandle === 'ne') {
        w = notaResizeStart.width + deltaX
        h = notaResizeStart.height - deltaY
        y = notaResizeStart.boxY + (notaResizeStart.height - Math.max(64, h))
      } else if (notaResizeHandle === 'nw') {
        w = notaResizeStart.width - deltaX
        h = notaResizeStart.height - deltaY
        x = notaResizeStart.boxX + (notaResizeStart.width - Math.max(80, w))
        y = notaResizeStart.boxY + (notaResizeStart.height - Math.max(64, h))
      }
      commitNotaBox(resizingNota, { x, y, w, h, width: canvasRect.width, height: canvasRect.height })
      return
    }
    if (!draggedNota) return
    e.preventDefault()
    commitNotaBox(draggedNota, {
      x: coords.clientX - canvasRect.left - notaDragOffset.x,
      y: coords.clientY - canvasRect.top - notaDragOffset.y,
      w: draggedNota.wRatio * canvasRect.width,
      h: draggedNota.hRatio * canvasRect.height,
      width: canvasRect.width,
      height: canvasRect.height
    })
  }

  const handleNotaMouseUp = () => {
    setDraggedNota(null)
    setNotaDragOffset({ x: 0, y: 0 })
    setResizingNota(null)
    setNotaResizeHandle(null)
  }

  useEffect(() => {
    if (!draggedNota && !resizingNota) return undefined
    document.addEventListener('mousemove', handleNotaMouseMove)
    document.addEventListener('mouseup', handleNotaMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleNotaMouseMove)
      document.removeEventListener('mouseup', handleNotaMouseUp)
    }
  }, [draggedNota, notaDragOffset, resizingNota, notaResizeHandle, notaResizeStart])

  return (
    <div id="tour-pdf-viewer" className="pdf-container flex h-full min-h-0 w-full min-w-0 flex-col">
      {/* Controls */}
      <div className="mb-4 flex shrink-0 items-center justify-between rounded-lg bg-gray-50 p-4 relative z-50">
        <div id="tour-pdf-navigation" className="flex items-center space-x-2">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage <= 1}
            className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Anterior
          </button>
          <span className="text-sm text-gray-600">
            Página {currentPage} de {totalPages}
          </span>
          <button
            onClick={handleNextPage}
            disabled={currentPage >= totalPages}
            className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Siguiente
          </button>
        </div>

        <div id="tour-news-pdf-zoom" className="flex items-center space-x-2">
          <button
            onClick={handleZoomOut}
            className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
          >
            -
          </button>
          <span className="text-sm text-gray-600">
            {Math.round((scale / REAL_SCALE) * 100)}%
          </span>
          <button
            onClick={handleZoomIn}
            disabled={scale >= REAL_SCALE * 2.5}
            className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            +
          </button>
        </div>
      </div>

      {/* Visor: sin mx-auto en el hijo directo (evita sesgo horizontal); scroll lo centra vía scrollLeft */}
      <div
        id="tour-news-pdf-scroll"
        ref={scrollContainerRef}
        className="mt-4 min-h-0 w-full min-w-0 max-w-full flex-1 overflow-auto"
      >
        <div className="relative overflow-visible rounded-xl bg-gray-50/60 pt-8 pb-6 w-max max-w-none">
          <div className="flex justify-center">
            <div className="relative inline-block">
              {systemConfig.show_signature_area_border && (
                <>
              <div className="pointer-events-none absolute inset-0 rounded-lg border-4 border-red-500 border-dashed z-20 shadow-[0_0_20px_rgba(239,68,68,0.4)]"></div>
              <div className="pointer-events-none absolute top-0 left-4 z-30 bg-red-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow">
                Área disponible para firma
                {canvasDimensions.displayWidth > 0 && (
                  <span className="ml-2 text-[10px] text-red-100 font-normal">
                    {Math.round(canvasDimensions.displayWidth)}px × {Math.round(canvasDimensions.displayHeight)}px
                  </span>
                )}
              </div>
                </>
              )}
              <div className={`relative inline-block bg-white rounded-lg overflow-hidden shadow-lg ${systemConfig.show_pdf_info_label ? 'border-4 border-dashed border-green-500 shadow-[0_0_25px_rgba(16,185,129,0.35)]' : ''}`}>
                {systemConfig.show_pdf_info_label && canvasDimensions.width > 0 && (
                  <div className="pointer-events-none absolute top-3 right-3 z-20 bg-green-600 text-white text-[11px] font-semibold px-3 py-1 rounded-full shadow">
                    PDF: {Math.round(canvasDimensions.width)} × {Math.round(canvasDimensions.height)} pt · Escala {((canvasDimensions.scale / REAL_SCALE) * 100).toFixed(0)}%
                  </div>
                )}
                {isLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
                      <p className="text-sm text-gray-600">Cargando página...</p>
                    </div>
                  </div>
                )}
                
                <div
                  className="relative inline-block"
                  onDragOver={(event) => {
                    const tipos = Array.from(event.dataTransfer.types || [])
                    const esNota = !!(onDropNota && tipos.includes('application/x-pdfsigner-nota'))
                    const esFirmante = !!(onDropFirmante && tipos.includes('application/x-pdfsigner-firmante'))
                    if (!esNota && !esFirmante) return
                    event.preventDefault()
                    event.dataTransfer.dropEffect = 'copy'
                    if (esNota) {
                      if (!notaSobrePdf) setNotaSobrePdf(true)
                    } else if (!firmanteSobrePdf) {
                      setFirmanteSobrePdf(true)
                    }
                  }}
                  onDragLeave={(event) => {
                    if (event.currentTarget.contains(event.relatedTarget)) return
                    setFirmanteSobrePdf(false)
                    setNotaSobrePdf(false)
                  }}
                  onDrop={(event) => {
                    const notaRaw = onDropNota ? event.dataTransfer.getData('application/x-pdfsigner-nota') : ''
                    if (notaRaw) {
                      event.preventDefault()
                      setNotaSobrePdf(false)
                      if (!canvasRef.current) return
                      let nota
                      try {
                        nota = JSON.parse(notaRaw)
                      } catch (error) {
                        return
                      }
                      const rect = canvasRef.current.getBoundingClientRect()
                      const x = event.clientX - rect.left
                      const y = event.clientY - rect.top
                      if (rect.width <= 0 || rect.height <= 0 || x < 0 || y < 0 || x > rect.width || y > rect.height) return
                      onDropNota({
                        texto: nota.texto,
                        autorNombre: nota.autorNombre,
                        x,
                        y,
                        canvasWidth: rect.width,
                        canvasHeight: rect.height
                      })
                      return
                    }
                    if (!onDropFirmante) return
                    event.preventDefault()
                    setFirmanteSobrePdf(false)
                    const raw = event.dataTransfer.getData('application/x-pdfsigner-firmante')
                    if (!raw || !canvasRef.current) return
                    let firmante
                    try {
                      firmante = JSON.parse(raw)
                    } catch (error) {
                      return
                    }
                    const rect = canvasRef.current.getBoundingClientRect()
                    const x = event.clientX - rect.left
                    const y = event.clientY - rect.top
                    if (rect.width <= 0 || rect.height <= 0 || x < 0 || y < 0 || x > rect.width || y > rect.height) return
                    onDropFirmante({
                      email: firmante.email,
                      nombre: firmante.nombre,
                      tempFirmanteId: firmante.temp_id,
                      x,
                      y,
                      canvasWidth: rect.width,
                      canvasHeight: rect.height
                    })
                  }}
                >
                  <canvas
                    ref={canvasRef}
                    className="block"
                    style={{ display: isLoading ? 'none' : 'block' }}
                  />
                  
                  {/* MARCADORES DE REFERENCIA: Cuadrícula y X roja en X=100, Y=100 (en puntos PDF) */}
                  {canvasDimensions.width > 0 && (systemConfig.show_calibration_grid || systemConfig.show_calibration_markers) && (
                    <div
                      className="absolute inset-0 pointer-events-none z-10"
                      style={{
                        width: `${canvasDimensions.width}px`,
                        height: `${canvasDimensions.height}px`
                      }}
                    >
                      {/* Cuadrícula de referencia cada 50 puntos */}
                      {systemConfig.show_calibration_grid && (
                        <svg
                          width={canvasDimensions.width}
                          height={canvasDimensions.height}
                          style={{ position: 'absolute', top: 0, left: 0 }}
                        >
                          {/* Líneas verticales cada 50 puntos */}
                          {Array.from({ length: Math.ceil(canvasDimensions.width / (50 * canvasDimensions.scale)) }).map((_, i) => (
                            <line
                              key={`v-${i}`}
                              x1={i * 50 * canvasDimensions.scale}
                              y1={0}
                              x2={i * 50 * canvasDimensions.scale}
                              y2={canvasDimensions.height}
                              stroke="#d1d5db"
                              strokeWidth="0.5"
                            />
                          ))}
                          {/* Líneas horizontales cada 50 puntos */}
                          {Array.from({ length: Math.ceil(canvasDimensions.height / (50 * canvasDimensions.scale)) }).map((_, i) => (
                            <line
                              key={`h-${i}`}
                              x1={0}
                              y1={i * 50 * canvasDimensions.scale}
                              x2={canvasDimensions.width}
                              y2={i * 50 * canvasDimensions.scale}
                              stroke="#d1d5db"
                              strokeWidth="0.5"
                            />
                          ))}
                        </svg>
                      )}
                      
                      {/* PUNTOS DE REFERENCIA: 4 marcadores X rojos en diferentes posiciones */}
                      {/* IMPORTANTE: La ESQUINA SUPERIOR IZQUIERDA de la firma imagen debe estar exactamente sobre la X roja */}
                      {systemConfig.show_calibration_markers && [
                        { x: 100, y: 100, label: 'Punto 1' },
                        { x: 400, y: 100, label: 'Punto 2' },
                        { x: 100, y: 600, label: 'Punto 3' },
                        { x: 400, y: 600, label: 'Punto 4' }
                      ].map((punto, index) => (
                        <div
                          key={index}
                          className="absolute"
                          style={{
                            left: `${punto.x * canvasDimensions.scale}px`,
                            top: `${punto.y * canvasDimensions.scale}px`,
                            transform: 'translate(-50%, -50%)',
                            zIndex: 30
                          }}
                        >
                          {/* X roja grande */}
                          <svg width="40" height="40" viewBox="0 0 40 40">
                            <line x1="5" y1="5" x2="35" y2="35" stroke="#ef4444" strokeWidth="3" />
                            <line x1="5" y1="35" x2="35" y2="5" stroke="#ef4444" strokeWidth="3" />
                            <circle cx="20" cy="20" r="22" fill="none" stroke="#ef4444" strokeWidth="2" />
                          </svg>
                          {/* Etiqueta de texto con instrucciones claras */}
                          <div className="absolute top-10 left-1/2 transform -translate-x-1/2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded whitespace-nowrap text-center">
                            <div>{punto.label}: X={punto.x}, Y={punto.y}</div>
                            <div className="text-[10px] font-normal mt-1 border-t border-red-500 pt-1">
                              ⬆️ ESQUINA SUPERIOR IZQUIERDA
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Signature Overlays */}
                  {signatures.map((signature) => {
                    return (
                    <div
                      key={signature.id}
                      className="signature-container absolute border-2 border-primary-500 bg-primary-50 bg-opacity-50 rounded cursor-move select-none"
                      style={{
                        touchAction: 'none',
                        WebkitUserSelect: 'none',
                        userSelect: 'none',
                        left: signature.x,
                        top: signature.y,
                        width: signature.width || (signature.type === 'text' ? 'auto' : 200),
                        height: signature.height || (signature.type === 'text' ? 34 : 67),
                        zIndex: 20,
                        overflow: signature.type === 'imagen' ? 'hidden' : 'visible',
                        minWidth: signature.type === 'text' ? 'fit-content' : (signature.width || 200),
                        minHeight: signature.type === 'text' ? 34 : (signature.height || 67)
                      }}
                      onMouseDown={(e) => handleMouseDown(e, signature)}
                      onTouchStart={(e) => handleTouchStart(e, signature)}
                    >
                      {/* Handles de redimensionamiento en las 4 esquinas */}
                      <div
                        className="resize-handle absolute -top-1 -left-1 w-3 h-3 bg-primary-600 border-2 border-white rounded-full cursor-nwse-resize z-30"
                        data-handle="nw"
                        style={{ touchAction: 'none' }}
                      />
                      <div
                        className="resize-handle absolute -top-1 -right-1 w-3 h-3 bg-primary-600 border-2 border-white rounded-full cursor-nesw-resize z-30"
                        data-handle="ne"
                        style={{ touchAction: 'none' }}
                      />
                      <div
                        className="resize-handle absolute -bottom-1 -left-1 w-3 h-3 bg-primary-600 border-2 border-white rounded-full cursor-nesw-resize z-30"
                        data-handle="sw"
                        style={{ touchAction: 'none' }}
                      />
                      <div
                        className="resize-handle absolute -bottom-1 -right-1 w-3 h-3 bg-primary-600 border-2 border-white rounded-full cursor-nwse-resize z-30"
                        data-handle="se"
                        style={{ touchAction: 'none' }}
                      />
                      <div className="flex items-center h-full" style={{ 
                        minWidth: signature.type === 'text' ? 'fit-content' : 'auto',
                        gap: '4px',
                        width: signature.type === 'imagen' ? '100%' : 'auto',
                        height: signature.type === 'imagen' ? '100%' : 'auto',
                        // Texto: alineación izquierda para que signature.x = inicio real del texto
                        // Imagen: centrada dentro del contenedor
                        justifyContent: signature.type === 'imagen' ? 'center' : 'flex-start',
                        padding: signature.type === 'imagen' ? '2px' : '0',
                        boxSizing: 'border-box'
                      }}>
                        <div style={{ 
                          whiteSpace: signature.type === 'text' ? 'nowrap' : 'normal',
                          overflow: signature.type === 'imagen' ? 'hidden' : 'visible',
                          flexShrink: 0,
                          width: signature.type === 'imagen' ? '100%' : 'auto',
                          height: signature.type === 'imagen' ? '100%' : 'auto',
                          display: 'flex',
                          alignItems: 'center',
                          // Texto: izquierda. Imagen: centrada.
                          justifyContent: signature.type === 'imagen' ? 'center' : 'flex-start',
                          lineHeight: signature.type === 'text' ? '1' : 'normal',
                          textAlign: signature.type === 'text' ? 'left' : 'center'
                        }}>
                          {signature.type === 'text' ? (
                            <span 
                              className="signature-text font-bold" 
                              style={{ 
                                whiteSpace: 'nowrap',
                                fontFamily: signature.font || 'Dancing Script, cursive',
                                fontSize: signature.width && signature.height 
                                  ? `${(signature.height * 0.8)}px`
                                  : '1.6rem'
                              }}
                            >
                              {signature.text}
                            </span>
                          ) : (
                            <img
                              src={signature.imageUrl}
                              alt="Firma"
                              className="object-contain"
                              style={{ 
                                height: '100%',
                                width: 'auto',
                                maxWidth: '100%'
                              }}
                              draggable={false}
                              onError={(e) => {
                                console.error('Error al cargar imagen de firma:', signature.imageUrl)
                                e.target.style.display = 'none'
                                const parent = e.target.parentElement
                                if (parent) {
                                  parent.innerHTML = '<span class="text-red-500 text-xs">Error al cargar imagen</span>'
                                }
                              }}
                            />
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            onRemoveSignature(signature.id)
                          }}
                          className="text-red-500 hover:text-red-700 text-xs flex-shrink-0"
                          style={{ flexShrink: 0, marginLeft: '4px' }}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                    )
                  })}
                  {canvasDimensions.width > 0 && campos.map((campo) => {
                    const left = campo.xRatio * canvasDimensions.width
                    const top = campo.yRatio * canvasDimensions.height
                    const width = campo.wRatio * canvasDimensions.width
                    const height = campo.hRatio * canvasDimensions.height
                    const bloqueado = !!campo.bloqueado
                    const propio = !!campo.propio
                    return (
                      <div
                        key={campo.id}
                        data-campo-bloqueado={bloqueado ? '1' : '0'}
                        data-campo-propio={propio ? '1' : '0'}
                        className={`absolute border-2 rounded select-none overflow-hidden ${bloqueado ? 'pointer-events-none' : 'cursor-move'} ${bloqueado && !propio ? 'border-dashed' : ''}`}
                        style={{
                          left,
                          top,
                          width,
                          height,
                          zIndex: bloqueado ? (propio ? 16 : 12) : 15,
                          borderColor: campo.color || '#1d4ed8',
                          backgroundColor: propio || !bloqueado ? 'rgba(29, 78, 216, 0.16)' : 'rgba(107, 114, 128, 0.14)',
                          touchAction: bloqueado ? 'auto' : 'none'
                        }}
                        onMouseDown={bloqueado ? undefined : (e) => handleCampoMouseDown(e, campo)}
                      >
                        {!bloqueado && (
                          <>
                            <div className="campo-resize-handle absolute -top-1 -left-1 w-3 h-3 bg-white border-2 rounded-full cursor-nwse-resize" data-handle="nw" style={{ borderColor: campo.color || '#1d4ed8' }} />
                            <div className="campo-resize-handle absolute -top-1 -right-1 w-3 h-3 bg-white border-2 rounded-full cursor-nesw-resize" data-handle="ne" style={{ borderColor: campo.color || '#1d4ed8' }} />
                            <div className="campo-resize-handle absolute -bottom-1 -left-1 w-3 h-3 bg-white border-2 rounded-full cursor-nesw-resize" data-handle="sw" style={{ borderColor: campo.color || '#1d4ed8' }} />
                            <div className="campo-resize-handle absolute -bottom-1 -right-1 w-3 h-3 bg-white border-2 rounded-full cursor-nwse-resize" data-handle="se" style={{ borderColor: campo.color || '#1d4ed8' }} />
                          </>
                        )}
                        <div className="flex h-full flex-col justify-center px-1 text-xs font-medium leading-tight pointer-events-none" style={{ color: campo.color || '#1d4ed8' }}>
                          {bloqueado && propio && vistaFirma && !campo.completado ? (
                            <div data-vista-firma="1" className="flex h-full w-full items-center justify-center overflow-hidden">
                              {vistaFirma.type === 'imagen' && vistaFirma.imageUrl ? (
                                <img src={vistaFirma.imageUrl} alt="" draggable={false} className="max-h-full max-w-full object-contain" />
                              ) : (
                                <span className="max-w-full truncate text-center" style={{ fontFamily: vistaFirma.font || 'cursive', fontSize: Math.max(10, Math.min(28, height * 0.45)), lineHeight: 1.1 }}>
                                  {vistaFirma.text || 'Firma'}
                                </span>
                              )}
                            </div>
                          ) : bloqueado ? (
                            <>
                              <span className="truncate font-semibold">{propio ? `Tu firma · ${campo.nombre || 'Firmante'}` : `Firma de ${campo.nombre || 'otro firmante'}`}</span>
                              <span className="truncate">
                                {propio
                                  ? (campo.estado_firmante === 'rechazado' ? 'Rechazado' : 'Aquí debes firmar')
                                  : (campo.estado_firmante === 'rechazado'
                                    ? 'Rechazado'
                                    : (campo.completado ? 'Completado' : 'Pendiente'))}
                              </span>
                            </>
                          ) : (
                            <span className="truncate">{campo.etiqueta || 'Campo'} · Firma</span>
                          )}
                        </div>
                        {!bloqueado && (
                          <button
                            type="button"
                            className="absolute top-0 right-0 px-1 text-red-600 text-sm leading-none"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation()
                              if (onRemoveCampo) onRemoveCampo(campo.id)
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    )
                  })}
                  {canvasDimensions.width > 0 && notas.filter((nota) => nota.page === currentPage).map((nota) => {
                    const left = nota.xRatio * canvasDimensions.width
                    const top = nota.yRatio * canvasDimensions.height
                    const width = nota.wRatio * canvasDimensions.width
                    const height = nota.hRatio * canvasDimensions.height
                    const fija = !!nota.guardada
                    const fecha = nota.fechaCreacion
                      ? new Date(nota.fechaCreacion).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })
                      : ''
                    return (
                      <div
                        key={nota.id}
                        data-nota-guardada={fija ? '1' : '0'}
                        className={`absolute overflow-hidden select-none bg-white/80 ${fija ? 'pointer-events-none' : 'cursor-move rounded-sm'}`}
                        style={{ left, top, width, height, zIndex: fija ? 11 : 14, touchAction: fija ? 'auto' : 'none', boxShadow: fija ? 'none' : 'inset 0 0 0 1px rgba(0,0,0,0.12)' }}
                        onMouseDown={fija ? undefined : (e) => handleNotaMouseDown(e, nota)}
                      >
                        {!fija && (
                          <>
                            <div className="nota-resize-handle absolute -top-1 -left-1 w-3 h-3 bg-white border border-gray-400 rounded-full cursor-nwse-resize" data-handle="nw" />
                            <div className="nota-resize-handle absolute -top-1 -right-1 w-3 h-3 bg-white border border-gray-400 rounded-full cursor-nesw-resize" data-handle="ne" />
                            <div className="nota-resize-handle absolute -bottom-1 -left-1 w-3 h-3 bg-white border border-gray-400 rounded-full cursor-nesw-resize" data-handle="sw" />
                            <div className="nota-resize-handle absolute -bottom-1 -right-1 w-3 h-3 bg-white border border-gray-400 rounded-full cursor-nwse-resize" data-handle="se" />
                          </>
                        )}
                        <div className="pointer-events-none flex h-full flex-col px-1.5 py-1">
                          <div className="truncate text-[10px] font-semibold text-gray-900">
                            Nota de {nota.autorNombre || 'Autor'}
                          </div>
                          <p className="flex-1 overflow-hidden whitespace-pre-wrap text-[11px] leading-snug text-gray-900">{nota.texto}</p>
                          <div className="truncate text-[9px] text-gray-500">{fija ? fecha : 'Sin guardar'}</div>
                        </div>
                        {!fija && onRemoveNota && (
                          <button
                            type="button"
                            className="absolute top-0 right-0 px-1 text-gray-500 text-sm leading-none"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation()
                              onRemoveNota(nota.id)
                            }}
                          >
                            ×
                          </button>
                        )}
                      </div>
                    )
                  })}
                  {notaSobrePdf && (
                    <div className="pointer-events-none absolute inset-0 z-20 border border-gray-400 bg-white/30">
                      <span className="absolute top-2 left-1/2 -translate-x-1/2 text-xs font-medium text-gray-700 bg-white/95 px-2 py-1 rounded shadow-sm">
                        Suelta aquí para colocar la nota
                      </span>
                    </div>
                  )}
                  {firmanteSobrePdf && (
                    <div className="pointer-events-none absolute inset-0 z-20 border-2 border-primary-400 bg-primary-50/30">
                      <span className="absolute top-2 left-1/2 -translate-x-1/2 text-xs font-medium text-primary-700 bg-white/95 px-2 py-1 rounded shadow-sm">
                        Suelta aquí para agregar campo de firma
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  </div>
  )
}

export default PDFViewer

